// API client — wraps createAuthFetch for all core-api endpoints.

// The framework-free entry (layered shell surfaces, D19): the loader imports this module before
// any React code exists, so nothing here may reach `@zion/sdk-react`'s root barrel.
import {
  createAuthFetch,
  getAccessToken,
  mintAppToken,
  REQUEST_HEADER_TOO_LARGE_STATUS,
  type MaintenanceHandler,
  type TokenSource,
} from '@zion/sdk-react/auth-core';
import { extractOAuthErrorCode, parseErrorMessage } from '../logic/api-utils.js';
import {
  RETIRED_STATUS,
  SESSION_TRANSPORT_COOKIE,
  SESSION_TRANSPORT_HEADER,
  isDeprecationResponse,
  permissionKeysOf,
  type DeprecationResponse,
  type AuthorizeRequestView,
  type KernelOriginRequiredBody,
} from '@zion/sdk-contracts';
import { ADMIN_API_PREFIX } from '@zion/sdk-dp-admin-contracts';
import {
  RESERVED_PLATFORM_PREFIXES,
  matchesPrefix,
  surfaceDeclarationsSchemaForApp,
  type ShellEntryDeclaration,
} from '@zion/sdk-cp-dp-contracts';
import {
  ensureAppToken,
  evictAppToken,
  getAppToken,
  pendingAppToken,
  type MintedAppToken,
} from '../auth/app-token-store.js';
import type {
  LoginRequest,
  LoginResponse,
  ProvidersResponse,
  MyProfile,
  PlatformStatus,
  AppBundle,
  AppRegistryEntry,
  SetupStatus,
  SetupTokenVerifyResponse,
  SetupCompleteDto,
  SetupCompleteResponse,
  TestProviderRequest,
  TestProviderResponse,
  ProviderCatalogEntry,
  RecoveryLoginRequest,
  RecoveryLoginResponse,
} from './types.js';
import type { MyChannelsResponse } from '@zion/sdk-contracts';
import { SHELL_ATTEMPT_TIMEOUT_MS, createBodyDeadline } from '@zion/shell-system-surfaces/body-deadline';
import { recordAppRegistry } from './app-registry-snapshot.js';
import { activeKernelOrigin, type KernelOrigin } from '../kernel/kernel-origin.js';
import { disposedError } from '@zion/surface-host';

/**
 * ⭐ `body` carries the SERVER'S PARSED ERROR PAYLOAD, and it is exported for that reason.
 *
 * `parseErrorMessage` reduces a body to its `message` string. That is right for most
 * endpoints and wrong for the ones that throw a STRUCTURED `BadRequestException({ code, … })`
 * — Nest uses the object AS the whole response body, so there is no `message` key, the
 * message falls back to `res.statusText`, and the operator is shown "Bad Request" while the
 * actionable content is discarded. `POST …/canary/advance` is the worst case: its
 * `incomplete_fixture_coverage` error carries `missingFixtures[]`, which is the entire point
 * of the error (#1132).
 *
 * Additive: `message` and `statusCode` are unchanged, so every existing caller and
 * `errorMessage()` behave exactly as before.
 */
export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Thrown when core-api returns 410 with a canonical `DeprecationResponse`
 * body — i.e. the server has retired the endpoint via the
 * `@RetiredEndpoint` decorator from `@zion/sdk-nestjs`. Carries the full
 * deprecation payload so callers can route to a replacement endpoint or
 * surface a "this feature has moved" panel without a second fetch.
 *
 * Surfaces only via the strict `request()` helper. The shell-side
 * `safeRequest()` wrapper catches this and returns a `RetiredEndpoint`
 * sentinel so query/mutation hooks can branch without try/catch.
 */
/**
 * ⛔ THE ONE SENTENCE FOR A `431`, AND EVERY SURFACE SAYS IT. It is the
 * `ApiError.message` the transport wrappers throw, so a toast, a banner and a
 * `console` line all carry the same words; it is also the copy the absence
 * arms render, so the hero paragraph and the version chip's hint cannot drift
 * apart from each other or from the error.
 *
 * Three things it must not say, each of them measured (#2864, #2866):
 *
 *  - **Not "reload".** The refusal is decided by the bytes of the request's own
 *    `Authorization` header, so the next load sends the same bytes and is
 *    refused again. The shipped errorstate copy did say reload, which sent
 *    every affected reader to a remedy that cannot work.
 *  - **Not "sign out and in again".** A new login mints a token at least as
 *    large as the one it replaces, and `POST /v1/auth/logout` carries the
 *    oversized bearer too — five of them 431'd in the measured outage window,
 *    so the sign-out itself fails.
 *  - **Not "temporary".** Nothing about it passes with time.
 *
 * It names the status because a reader who can quote `431` gives their
 * administrator something to search for, exactly as #2571's copy names the
 * `settings:read` grant so a blocked reader can ask for it by name.
 */
export const SESSION_TOO_LARGE_MESSAGE =
  'Your sign-in session is too large for the server to accept, so this request was refused. ' +
  'Reloading will not help, and signing in again makes the session larger. ' +
  'Ask an administrator to look into HTTP 431 on this data plane.';

export class EndpointRetiredError extends Error {
  constructor(
    public readonly path: string,
    public readonly deprecation: DeprecationResponse,
  ) {
    super(`Endpoint retired: ${path} — ${deprecation.message}`);
    this.name = 'EndpointRetiredError';
  }
}

/**
 * Sentinel returned by `safeRequest()` when an endpoint has been retired.
 * Forward-looking — no current core-api endpoint emits 410 yet, but every
 * future deprecation goes through this contract (SYS / SDK invariant
 * pair). Hooks branch on `'retired' in result` to render a "this feature
 * has moved" surface (linked to `replacement` when present).
 */
export interface RetiredEndpoint {
  readonly retired: true;
  readonly replacement?: string;
}

let authFetch: typeof fetch = fetch;

/**
 * The session-bearer fetch with no attempt deadline: the reserved arm of an app mount's
 * {@link createAppFetch}. The same session path as `authFetch` (401 refresh, cold start,
 * maintenance), so an app's call to a reserved prefix such as `/v1/me` keeps the session
 * bearer, and a stream on it is never cut. The global `fetch` before `initApiClient` runs.
 */
let sessionFetchNoDeadline: typeof fetch = fetch;

/**
 * The handler `initApiClient` was given, kept so {@link createAppFetch} builds
 * each app's fetch with the same maintenance overlay the shell's own fetch
 * has. `undefined` before `initApiClient` runs, which is how `createAuthFetch`
 * reads "return a 503 to the caller as it is".
 */
let maintenanceHandler: MaintenanceHandler | undefined;

/** The deadline the last good `initApiClient` call set; read by {@link shellAttemptTimeoutMs}. */
let attemptTimeoutMsInUse = SHELL_ATTEMPT_TIMEOUT_MS;

/**
 * The per-attempt deadline the shell's session fetch carries now, in milliseconds: the value the
 * last `initApiClient` call set, `SHELL_ATTEMPT_TIMEOUT_MS` (from
 * `@zion/shell-system-surfaces/body-deadline`) in production and before any call. A call that
 * throws `RangeError` leaves it unchanged. It has no side effect.
 *
 * The shell's JSON clients read it per call and pass it to `createBodyDeadline`, which bounds the
 * body read after the headers (R-127). So for each of them one value bounds both the wait for the
 * headers and the wait for the body:
 *
 * - `request()` and `oauthFlowRequest()` in this module;
 * - `request()` and `deleteIdempotent()` in `home/api/home-api.ts`;
 * - `fetchTrailerManifest` in `home/api/manifest.ts`.
 *
 * `git grep -n createBodyDeadline -- cloud/dp/shell/src` lists them, so the grep is the census
 * when this list and the tree disagree.
 *
 * The surface services of `@zion/surface-host` (`createCoreApiRequest`,
 * `createRootShellSettingsSource`) do not read it either: their body bound is
 * `SERVICES_FETCH_TIMEOUT_MS`, `SHELL_ATTEMPT_TIMEOUT_MS` in production (kernel apps WS-16d). Their
 * header deadline is this module's, because the shell policy's `platformFetch` is
 * `sharedAuthFetch`.
 *
 * `system-api.ts` in `@zion/shell-system-surfaces` does not read it. Its body bound is the
 * `bodyTimeoutMs` of `createSystemApi`, `SHELL_ATTEMPT_TIMEOUT_MS` in production, so a shortened
 * shell deadline leaves the system mount body bound at 30 seconds.
 */
export function shellAttemptTimeoutMs(): number {
  return attemptTimeoutMsInUse;
}

/**
 * Wire the shell's session fetches to the maintenance overlay. Boot calls it once.
 *
 * @param maintenance The overlay `createAuthFetch` drives on a 503 with `Retry-After`.
 * @param attemptTimeoutMs The deadline of one network attempt of the shell's own fetch,
 *   `authFetch` behind {@link sharedAuthFetch}, `request()` and
 *   `oauthFlowRequest()`. `SHELL_ATTEMPT_TIMEOUT_MS` in production; a test shortens it. A value
 *   that is not a finite number above 0 and at most 2147483647 throws a `RangeError` from
 *   `createAuthFetch`. The same value bounds the body read of the shell clients that
 *   {@link shellAttemptTimeoutMs} names. The body bound of `system-api.ts` stays
 *   `SHELL_ATTEMPT_TIMEOUT_MS`.
 *
 * It also builds the no-deadline session fetch an app mount's reserved arm uses. A later call
 * replaces both; every wrapper in this module reads them live. A call that throws changes no
 * module value: the overlay, both session fetches and the deadline stay as the last good call
 * set them.
 */
export function initApiClient(
  maintenance: MaintenanceHandler,
  attemptTimeoutMs: number = SHELL_ATTEMPT_TIMEOUT_MS,
): void {
  // Build both closures first. `createAuthFetch` refuses a bad deadline with a `RangeError`, and
  // the throw must leave every module value as it was.
  const nextAuthFetch = createAuthFetch(maintenance, undefined, undefined, { attemptTimeoutMs });
  const nextSessionFetchNoDeadline = createAuthFetch(maintenance);
  maintenanceHandler = maintenance;
  authFetch = nextAuthFetch;
  sessionFetchNoDeadline = nextSessionFetchNoDeadline;
  attemptTimeoutMsInUse = attemptTimeoutMs;
}

/**
 * Stable wrapper around the live `authFetch` reference. The wrapper is
 * frozen at module import time, and every call reads the current
 * `authFetch`. So a caller holds one reference, and that reference follows
 * each `initApiClient()` call with no re-render. The callers are the shell's
 * own clients:
 * - `request()` and `deleteIdempotent()` in `home/api/home-api.ts`;
 * - `fetchTrailerManifest`;
 * - the app token exchange (`mintAppTokenFor` here and `mintFor` in
 *   `boot/shell-services.ts`);
 * - the shell policy's `platformFetch`. Four parts of `@zion/surface-host`
 *   send through it: the branding controller, the preferences store, the
 *   root-shell settings source and the app-open record.
 *
 * Use this when a shell context needs a stable function identity. Each
 * network attempt carries the deadline `initApiClient` set
 * (`SHELL_ATTEMPT_TIMEOUT_MS`), which stops at the response headers. The
 * maintenance retry of a request that is not safe to send again
 * (`isColdStartRetryable`) has none (R-127 item 4). A hosted app gets
 * {@link createAppFetch}, never this.
 *
 * It reads the target once with `readFetchTarget`, then classifies and sends
 * that one value, a string target as the absolute URL it resolved to. A
 * target that cannot be read rejects the call with the error of the read and
 * sends nothing, as the global `fetch` does. A `Request` of another realm goes
 * to the global `fetch` as it is, with no bearer (R-125).
 *
 * It hands `init` on as it came, never a copy. The preferences store of
 * `@zion/surface-host` sends through this function, and its
 * `createCoreApiRequest` puts the bearer check of each network attempt
 * behind the `signal` getter of `init`. A copy reads that getter once, so a
 * maintenance retry would leave with no new check (kernel apps WS-16d R7;
 * `boot/__tests__/shell-services.test.ts` holds the case).
 */
export const sharedAuthFetch: typeof fetch = (input, init) =>
  withFetchTarget(input, init, (routed) => guardedAuthFetch(routed, init));

/**
 * The target of one routed fetch, read once by `readFetchTarget` (WS-16b
 * review finding R1, R-125).
 *
 * - `target` is the bearer class of `send`, by the rule of
 *   {@link classifyAppFetchTarget}.
 * - `send` is the value the network call sends: the absolute URL that a
 *   string resolved to against `window.location.href`, the text itself when
 *   no base resolves it, or a copy of a `Request` of this realm.
 *
 * The classifier read `send` and nothing else, so the class and the request
 * name the same URL. An absolute URL leaves nothing for the browser to
 * resolve, so a `<base>` element cannot move a classified call to another
 * origin (WS-14a E-16, the security branch S3 of `createMountFetch`).
 */
interface RoutedTarget {
  readonly target: AppFetchTarget;
  readonly send: string | Request;
}

/**
 * True for a `Request` of another realm, such as an iframe or another
 * window: a value that `instanceof Request` refuses and whose
 * `Object.prototype.toString` tag reads `Request`. The shell cannot read its
 * URL through its own `Request` reader, so the value takes the class that
 * carries no bearer (R-125). A value that forges the tag takes that class too,
 * which only takes a bearer away.
 *
 * It throws what the read of the prototype chain or of the tag throws, as a
 * `Proxy` can.
 */
function isForeignRequest(input: RequestInfo | URL): boolean {
  return !(input instanceof Request) && Object.prototype.toString.call(input) === '[object Request]';
}

/**
 * The bearer class of a URL that resolved against `window.location.href`.
 * The order is `'cross-origin'`, `'kernel-only'`, `'reserved'`, `'app'`, as
 * {@link classifyAppFetchTarget} states.
 */
function classOfResolved(resolved: URL): AppFetchTarget {
  if (resolved.origin !== window.location.origin) return 'cross-origin';
  if (matchesPrefix(resolved.pathname, ADMIN_API_PREFIX)) return 'kernel-only';
  return RESERVED_PLATFORM_PREFIXES.some((prefix) => matchesPrefix(resolved.pathname, prefix))
    ? 'reserved'
    : 'app';
}

/**
 * Resolve a URL text once and classify it: the one rule behind
 * {@link classifyAppFetchTarget} and the routed fetches.
 *
 * The text resolves against `window.location.href`, the document URL (R-029,
 * R-033). One base is one rule, and `href` rather than `origin` is what
 * reproduces the browser's pathname for a document-relative target such as
 * `notes/42`.
 *
 * The base is what makes the protocol-relative form cross-origin. Without
 * it `new URL('//evil.com/steal')` throws, and the DP bearer reaches the
 * admin-chosen host the cross-origin class exists to keep it away from
 * (security-audit.md SEC-7, R-029).
 *
 * @returns The class and the absolute URL to send. A text no base can resolve
 *   answers `'app'` and the text itself: it has no origin of its own, so it is
 *   same-origin by construction and reaches no reserved or admin route. It
 *   never throws.
 */
function resolveTargetText(text: string): RoutedTarget {
  let resolved: URL;
  try {
    resolved = new URL(text, window.location.href);
  } catch {
    // Unparseable against the document: it reached no origin of its own, so
    // it can reach no reserved route either.
    return { target: 'app', send: text };
  }
  return { target: classOfResolved(resolved), send: resolved.href };
}

/**
 * Read the target of a `fetch` call once, in the form the network call will
 * send (WS-16b review finding R1, R-125).
 *
 * - A string resolves once against `window.location.href`, and the absolute
 *   URL is what leaves.
 * - A `Request` of this realm is copied with `new Request(input)`. The copy
 *   takes the URL, the method, the headers, the body and the signal of the
 *   input, and its `url` is the platform's own reader. So a subclass that
 *   overrides `url` cannot show the classifier one path and send another. A
 *   body moves to the copy, as it moves into the request the global `fetch`
 *   makes. The copy's URL is already absolute.
 * - Any other value, a `URL` among them, becomes `String(input)`, the
 *   conversion the global `fetch` applies, and then resolves as a string. The
 *   string is immutable, so a later change to the `URL` (for example from an
 *   `init.headers` getter that `createAuthFetch` reads after the classifier)
 *   changes nothing that is sent.
 *
 * A `Request` of another realm never reaches it: `withFetchTarget` sends that
 * value to the global `fetch` first.
 *
 * It throws what the read throws: a `toString` that throws, or the
 * `TypeError` of `new Request` for a `Request` whose body a reader used.
 * It assumes the realm's own `Request` and `String` are intact; a bundle that
 * replaces the realm's built-ins is outside what a shell-side check can hold,
 * and the core-api kernel guard (WS-19) holds the guarantee.
 */
function readFetchTarget(input: RequestInfo | URL): RoutedTarget {
  if (typeof input === 'string') return resolveTargetText(input);
  if (input instanceof Request) {
    const copy = new Request(input);
    return { target: resolveTargetText(copy.url).target, send: copy };
  }
  return resolveTargetText(String(input));
}

/**
 * Run `send` on the target of a `fetch` call, read once by `readFetchTarget`.
 *
 * - A `Request` of another realm goes to the global `fetch` as it is, with
 *   `init`, and `send` never runs: the shell reads no URL from it and puts no
 *   bearer on it (R-125).
 * - A read that throws, the realm check included, gives a rejected promise
 *   and `send` never runs, so no request leaves: the global `fetch` rejects
 *   the same input the same way.
 */
function withFetchTarget(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  send: (routed: RoutedTarget) => Promise<Response>,
): Promise<Response> {
  let routed: RoutedTarget;
  try {
    if (typeof input !== 'string' && isForeignRequest(input)) return fetch(input, init);
    routed = readFetchTarget(input);
  } catch (err) {
    return Promise.reject(err);
  }
  return send(routed);
}

/**
 * Which bearer a per-app `fetch` target is entitled to (D-3, D-11, full-body
 * item 3 of `00-index.md` §4; kernel apps WS-16b, design section 3.7).
 *
 * - `'kernel-only'`: a same-origin path under `ADMIN_API_PREFIX` (`/v1/admin`).
 *   The admin routes belong to the kernel origin (locked decisions 8 and 12 of
 *   the kernel-apps overview). With the kernel origin on, every shell fetch
 *   answers it with a local 403 and sends nothing. With the switch off, it
 *   takes the `'reserved'` path and carries the SESSION token, as before.
 * - `'reserved'`: any other same-origin path under `RESERVED_PLATFORM_PREFIXES`.
 *   The shell's own routes and the app-token exchange. Carries the SESSION token.
 * - `'app'`: any other same-origin path. The hosted app's own backend.
 *   Carries the APP token.
 * - `'cross-origin'`: an origin other than the document's. Carries NO bearer.
 */
export type AppFetchTarget = 'reserved' | 'app' | 'cross-origin' | 'kernel-only';

/**
 * Classify a `fetch` target into the four bearer classes (D-11, R-033, WS-16b).
 *
 * Callers: tests and any shell code that needs the class of a target without
 * a send. The routed fetches ({@link createAppFetch}, {@link sharedAuthFetch}
 * and the JSON clients of this module) apply the
 * same rule to the one value they send, which `readFetchTarget` reads once
 * (WS-16b review finding R1).
 *
 * @param input The target a caller handed `fetch`. A string is read as it is,
 *   a `Request` of this realm by its `url`, and any other value, a `URL` among
 *   them, by `String(input)`, the conversion the global `fetch` applies. This
 *   function copies no `Request`, so it leaves the body of the caller's
 *   `Request` unread. The text resolves against `window.location.href`.
 * @returns In this order:
 *   1. `'cross-origin'` when the resolved origin differs from
 *      `window.location.origin`, which covers the protocol-relative
 *      `//host/path` form;
 *   2. `'kernel-only'` when the resolved pathname matches `ADMIN_API_PREFIX`
 *      under `matchesPrefix`;
 *   3. `'reserved'` when it matches another entry of
 *      `RESERVED_PLATFORM_PREFIXES` under `matchesPrefix`;
 *   4. `'app'` for every other same-origin target.
 *   An input no base can resolve answers `'app'`: it reached no origin of its
 *   own, so it can reach no reserved or admin route either. Two inputs answer
 *   `'cross-origin'`, the class that carries no bearer, before any of these
 *   steps (R-125, defaults row "Refuse over accept"):
 *   - an input whose read throws: a `toString` or a `url` reader that throws,
 *     or a `Proxy` whose prototype read throws;
 *   - a `Request` of another realm, which `instanceof Request` refuses and
 *     whose `Object.prototype.toString` tag reads `Request`.
 *   The routed fetches send the second form to the global `fetch` with no
 *   bearer, and reject the first with the error of the read. The
 *   `classifyFetchTarget` of `@zion/surface-host` answers the first form
 *   alike.
 *
 * `ADMIN_API_PREFIX` comes from `@zion/sdk-dp-admin-contracts`, and
 * `RESERVED_PLATFORM_PREFIXES` and `matchesPrefix` from
 * `@zion/sdk-cp-dp-contracts`, so the shell declares no list of its own.
 * `matchesPrefix` is segment-aware: `/v1/authz` and `/v1/administer` are
 * `'app'`, `/v1/auth/app-token` is `'reserved'`, and `/v1/admin` and
 * `/v1/admin/users` are `'kernel-only'`. The answer does not depend on the
 * kernel switch; the fetches read the switch.
 *
 * It reads no store, performs no request, and returns one of the four values
 * for every input.
 */
export function classifyAppFetchTarget(input: RequestInfo | URL): AppFetchTarget {
  let text: string;
  try {
    if (typeof input === 'string') text = input;
    else if (input instanceof Request) text = input.url;
    else if (isForeignRequest(input)) return 'cross-origin';
    else text = String(input);
  } catch {
    // A target with no text that can be checked carries no bearer (R-125).
    return 'cross-origin';
  }
  return resolveTargetText(text).target;
}

/**
 * The local answer to a shell-realm fetch of an admin route while the kernel
 * origin is on (kernel apps WS-16b, design section 3.7, finding A of the
 * kernel-apps overview).
 *
 * Callers: {@link createAppFetch}, {@link sharedAuthFetch} and this
 * module's JSON clients, each for a
 * `'kernel-only'` target while `activeKernelOrigin()` is non-null.
 *
 * @param origin The active kernel origin, `https://kernel.<dp>`, from
 *   `activeKernelOrigin()`.
 * @returns A new `Response` on each call: status 403, header
 *   `content-type: application/json`, and the body
 *   `{ code: 'kernel_origin_required', action: 'open_kernel_origin', kernelOrigin: origin }`,
 *   the WS-1b `KernelOriginRequiredBody`. A caller that reads it with
 *   `kernelRefusalOf` of `@zion/sdk-react` gets `kernel-origin-required` and
 *   can open the kernel origin.
 *
 * It sends no request and reads no token. The body carries the origin and no
 * caller text. It never throws. Defence in depth only: a bundle can still call
 * the global `fetch`, and the core-api kernel guard (WS-19) holds the
 * guarantee.
 */
export function kernelOriginRefusal(origin: KernelOrigin): Response {
  const body: KernelOriginRequiredBody = {
    code: 'kernel_origin_required',
    action: 'open_kernel_origin',
    kernelOrigin: origin,
  };
  return new Response(JSON.stringify(body), {
    status: 403,
    headers: { 'content-type': 'application/json' },
  });
}

/**
 * The kernel origin when `routed` is a `'kernel-only'` target and the kernel
 * origin is on, else `null`. It reads `activeKernelOrigin()` per call, so the
 * answer follows the switch the boot set. Reads one module variable; sends
 * nothing.
 */
function kernelRefusalOriginFor(routed: RoutedTarget): KernelOrigin | null {
  const origin = activeKernelOrigin();
  return origin !== null && routed.target === 'kernel-only' ? origin : null;
}

/**
 * The session fetch `authFetch` behind the kernel guard: a `'kernel-only'`
 * target with the kernel origin on resolves {@link kernelOriginRefusal} before
 * any token read, and every other target goes to the live `authFetch`. So in
 * the `on` arm no shell code path puts the session bearer on a request to
 * `ADMIN_API_PREFIX` (WS-16b, design section 3.7, the invariant).
 *
 * `routed` is a value `readFetchTarget` made, from a caller target or from a
 * path string of this module. The guard reads its class and `authFetch` sends
 * its `send`, the value that class was read from, so no later read of a
 * caller object can move the request to another path (WS-16b review finding
 * R1). A path string leaves as an absolute URL (R-125). It hands `init` to
 * `authFetch` uncopied, for the reason {@link sharedAuthFetch} gives.
 */
function guardedAuthFetch(routed: RoutedTarget, init?: RequestInit): Promise<Response> {
  const origin = kernelRefusalOriginFor(routed);
  return origin === null ? authFetch(routed.send, init) : Promise.resolve(kernelOriginRefusal(origin));
}

/**
 * The hosted app's `fetch`, bound to that app's token (D-1, D-3, WS-2 §3.4).
 *
 * @param appUuid The app's `release.apps_catalog` id, `AppRegistryEntry.appUuid`,
 *   or `null` for a row the catalog does not back. A `null` uuid means the
 *   closure holds no app token and mints none (R-007).
 * @param attemptTimeoutMs The deadline of one network attempt, for a system
 *   mount only (R-127); `SHELL_ATTEMPT_TIMEOUT_MS` there. Absent, no
 *   attempt of the closure has a deadline, so a hosted app's stream stays open
 *   for as long as its server sends it. Present, the `'reserved'` arm goes to
 *   the session fetch behind {@link sharedAuthFetch}, whose deadline
 *   `initApiClient` set, and the
 *   `'app'` arm passes the value to `createAuthFetch` as `attemptTimeoutMs`.
 *   Either deadline stops at the response headers. A value that is not a
 *   finite number above 0 throws a `RangeError` from `createAuthFetch`.
 * @returns One closure per app. It reads each target once with
 *   `readFetchTarget`, classifies that value with the rule of
 *   {@link classifyAppFetchTarget}, and sends that same value on the arm the
 *   class names (R-053, WS-16b review finding R1). A string target leaves as
 *   the absolute URL it resolved to against `window.location.href`, so a
 *   `<base>` element cannot move it (R-125). A target whose read throws
 *   rejects with that error and sends nothing. A `Request` of another realm
 *   goes to the global `fetch` as it is, with no bearer (R-125). `'kernel-only'`
 *   reads `activeKernelOrigin()` per call: non-null answers
 *   {@link kernelOriginRefusal} and sends nothing (WS-16b), and `null`, the
 *   switch-off arm, takes the `'reserved'` route. `'reserved'` goes to the
 *   session fetch (the one behind {@link sharedAuthFetch} with a deadline, the no-deadline
 *   session fetch without one), `'cross-origin'` to the global `fetch`, and
 *   `'app'` to one `createAuthFetch` built with the live maintenance handler,
 *   `createAuthFetch`'s default cold-start bounds (the same ones
 *   {@link sharedAuthFetch} takes) and a `TokenSource` that carries the app
 *   token alone.
 *
 * The reserved arm keeps the session bearer on the shell's own routes and on
 * the exchange itself, and it keeps the session refresh: a 401 there is the
 * session path's verdict to make (D-3). The cross-origin arm attaches no
 * bearer, so none leaves the origin; today's `sharedAuthFetch` attaches one to
 * any origin, and this is what closes that.
 *
 * The app arm's `getToken` returns `getAppToken(appUuid)`, which is `null` for
 * an empty store and for a `null` uuid, so an app path with no stored token
 * carries no bearer. Its `renew` calls `evictAppToken(appUuid)` and then
 * `ensureAppToken(appUuid, mint)`, where `mint` posts through
 * `sharedAuthFetch` and maps the response to
 * `{ accessToken, expiresInSec: expiresIn }`, so the server's own lifetime
 * reaches the store; it resolves `null` for a `null` `appUuid`. The eviction
 * is what makes the re-mint unconditional (R-055, SEC-C2-3): without it
 * `ensureAppToken` returns the entry that just 401'd and the replay carries
 * the same rejected bearer. R-041 keeps
 * the request that 401'd on `renew`'s published signature, and this consumer
 * needs no argument: only an `'app'` target ever reaches the app arm. So a 401
 * on an app route re-mints from the current session token and a 401 on a
 * reserved route re-mints nothing; neither ends the user's session
 * (security-audit.md C4 hop 1). The app arm reads the store on every request,
 * so it picks up a re-mint between two calls without being rebuilt.
 *
 * The app arm waits for a mint in flight before it sends (#3291). With a uuid,
 * no stored token and a mint of that uuid in flight (`pendingAppToken`), the
 * send waits until the first of three: the mint settles; the caller's signal
 * aborts, which rejects with the signal's reason and sends nothing; or
 * `attemptTimeoutMs ?? shellAttemptTimeoutMs()` passes. When the mint settles
 * with no token stored and `pendingAppToken` then gives another mint (a
 * session change dropped the first and started a new one), the send waits on
 * that mint once, for what is left of the same bound. Then the send goes
 * through the app's `createAuthFetch`, which reads the store again. A mint that
 * gave no token, or a bound that passed, sends with no bearer and the 401 arm
 * re-mints, as before. The wait reads the entry of this uuid alone and starts
 * no mint. A stored token, a `null` uuid or no mint in flight sends at once.
 * A caller `Authorization` header, in `init.headers` or on a `Request` target,
 * also sends at once: `createAuthFetch` sends that header unchanged and never
 * puts the app token in its place. A throw from `init` (a getter, a method
 * that is not a string) rejects the call and sends nothing.
 *
 * `isRetained(appUuid)` answers whether a live mount still holds the app. The
 * shell policy passes its `retainApp` counts, and other callers get a
 * predicate that answers `true`. An app that the predicate does not retain
 * mints nothing and sends nothing more (push review pass 2):
 * - `renew` evicts the stored token and answers `null`, and posts no
 *   exchange. So a 401 reaches the caller as it is, and a cold-start or
 *   maintenance retry that reads no token stops with the answer it holds.
 * - A send that waited for the mint and finds the app not retained after the
 *   wait rejects with `disposedError('fetch')` of `@zion/surface-host`, the
 *   refusal of a mount fetch after its mount ended, and sends nothing.
 * A retained app's `renew` mints as above and keeps the new token.
 *
 * A GET or HEAD of the app arm to a string target whose `init` sets no `cache`
 * goes with `cache: 'no-store'`. A caller's `cache` wins, and a `Request`
 * target keeps its own mode. Every other arm hands `init` on as it came.
 */
export function createAppFetch(
  appUuid: string | null,
  attemptTimeoutMs?: number,
  isRetained: (appUuid: string) => boolean = () => true,
): typeof fetch {
  const source: TokenSource = {
    getToken: () => (appUuid === null ? null : getAppToken(appUuid)),
    renew: () => {
      if (appUuid === null) return Promise.resolve(null);
      // The server refused the bearer this entry holds, so drop it before
      // asking for one. Otherwise `ensureAppToken` hands back the token that
      // just 401'd and the replay carries it again.
      evictAppToken(appUuid);
      // No live mount holds the app, so it gets no new token and the call ends
      // on the answer it holds.
      if (!isRetained(appUuid)) return Promise.resolve(null);
      return ensureAppToken(appUuid, () => mintAppTokenFor(appUuid));
    },
  };
  const appAuthFetch = createAuthFetch(maintenanceHandler, undefined, source, { attemptTimeoutMs });
  const hasDeadline = attemptTimeoutMs !== undefined;

  // A fresh mount's first send waits for the mint `retainApp` started (#3291).
  // It reads this uuid's own entry; `createAuthFetch` then reads the store again.
  // `async`, so a throw from `init` (a getter, a non-string method) rejects, as before.
  const sendApp = async (send: string | Request, init: RequestInit | undefined): Promise<Response> => {
    const appInit = appSendInit(send, init);
    if (appUuid !== null && getAppToken(appUuid) === null && !callerSetsAuthorization(send, init)) {
      const pending = pendingAppToken(appUuid);
      if (pending !== null) {
        const boundMs = attemptTimeoutMs ?? shellAttemptTimeoutMs();
        await waitForAppMint(appUuid, pending, callerSignalFor(send, init), boundMs);
        // The mount ended during the wait, so the call refuses as a mount fetch does after its end.
        if (!isRetained(appUuid)) throw disposedError('fetch');
      }
    }
    return appAuthFetch(send, appInit);
  };

  // Every arm sends `routed.send`, the one value the switch classified. A
  // caller object is never read again after `readFetchTarget`, so a getter
  // cannot show the classifier one path and the network another (WS-16b
  // review finding R1).
  return (input, init) =>
    withFetchTarget(input, init, (routed) => {
      switch (routed.target) {
        case 'kernel-only': {
          // The admin routes belong to the kernel origin (WS-16b). With the
          // switch on, no request leaves and no bearer is read. With it off,
          // the target keeps the reserved route below.
          const origin = activeKernelOrigin();
          if (origin !== null) return Promise.resolve(kernelOriginRefusal(origin));
          return hasDeadline ? guardedAuthFetch(routed, init) : sessionFetchNoDeadline(routed.send, init);
        }
        case 'reserved':
          // The shell's own routes and the exchange itself keep the session
          // bearer, and a 401 on one is the session path's verdict to make. Only
          // a system mount's call carries the shell's deadline (R-127).
          return hasDeadline ? guardedAuthFetch(routed, init) : sessionFetchNoDeadline(routed.send, init);
        case 'cross-origin':
          // No bearer leaves the origin. Today's `sharedAuthFetch` attaches one
          // to any origin, and this is what closes that.
          return fetch(routed.send, init);
        default:
          return sendApp(routed.send, init);
      }
    });
}

/**
 * The init of one app-arm send: `init` plus `cache: 'no-store'` for a GET or
 * HEAD to a string target whose `init` sets no `cache`, else `init` as it came.
 * A `Request` target keeps the mode it carries, because the shell cannot tell
 * a caller's explicit `'default'` from none in every realm.
 */
function appSendInit(send: string | Request, init: RequestInit | undefined): RequestInit | undefined {
  if (typeof send !== 'string' || init?.cache !== undefined) return init;
  const method = (init?.method ?? 'GET').toUpperCase();
  return method === 'GET' || method === 'HEAD' ? { ...init, cache: 'no-store' } : init;
}

/**
 * The signal `fetch` obeys for this send: `init.signal` when it is not
 * `undefined` (`null` means none), else the signal of a `Request` target, else
 * none. The wait for a mint ends on the same signal.
 */
function callerSignalFor(send: string | Request, init: RequestInit | undefined): AbortSignal | null {
  if (init?.signal !== undefined) return init.signal;
  return typeof send === 'string' ? null : send.signal;
}

/**
 * Whether the caller set its own `Authorization` on this send. The headers
 * are the ones `fetch` reads: `init.headers` when it is not `undefined`, else
 * the headers of a `Request` target. `createAuthFetch` sends such a header
 * unchanged and never puts the app token in its place, so a wait for the mint
 * gives the send nothing.
 */
function callerSetsAuthorization(send: string | Request, init: RequestInit | undefined): boolean {
  if (init?.headers !== undefined) return new Headers(init.headers).has('Authorization');
  return typeof send !== 'string' && send.headers.has('Authorization');
}

/**
 * Wait for the app-token mint in flight before an app-arm send (#3291).
 *
 * @param pending The mint from `pendingAppToken`. It never rejects.
 * @param signal The caller's signal, or `null`.
 * @param boundMs The longest wait, in milliseconds.
 * @returns A promise that resolves when `pending` settles or `boundMs` passes,
 *   whichever comes first, and rejects with `signal.reason` when the signal
 *   aborts first or has already aborted. It reads no token, sends nothing, and
 *   clears its timer and its abort listener on every exit.
 */
function waitForMint(pending: Promise<string | null>, signal: AbortSignal | null, boundMs: number): Promise<void> {
  const watched = signal ?? new AbortController().signal;
  if (watched.aborted) return Promise.reject(watched.reason);
  return new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    function finish(): void {
      clearTimeout(timer);
      watched.removeEventListener('abort', onAbort);
    }
    function onAbort(): void {
      finish();
      reject(watched.reason);
    }
    function onDone(): void {
      finish();
      resolve();
    }
    // The listener goes first: when `addEventListener` throws, the wait rejects
    // before `waitForMint` arms its timer, so no timer outlives the call.
    watched.addEventListener('abort', onAbort, { once: true });
    timer = setTimeout(onDone, boundMs);
    void pending.then(onDone, onDone);
  });
}

/**
 * Wait for the app-token mint of one app, once more after a session change
 * (#3291, review finding architecture-1).
 *
 * @param appUuid The app whose mint the send waits for.
 * @param first The mint `pendingAppToken(appUuid)` gave before the wait.
 * @param signal The caller's signal, or `null`.
 * @param boundMs The longest wait over both mints, in milliseconds.
 * @returns A promise that resolves when the wait on `first` ends with a token
 *   stored, or with no other mint in flight. When `pendingAppToken(appUuid)`
 *   then gives a mint other than `first` (a session change dropped `first`
 *   and started it), it waits on that mint for what is left of `boundMs`, and
 *   reads no third mint. It rejects with `signal.reason` when the signal
 *   aborts. It reads no token value and sends nothing.
 */
async function waitForAppMint(
  appUuid: string,
  first: Promise<string | null>,
  signal: AbortSignal | null,
  boundMs: number,
): Promise<void> {
  const deadlineMs = Date.now() + boundMs;
  await waitForMint(first, signal, boundMs);
  if (getAppToken(appUuid) !== null) return;
  const next = pendingAppToken(appUuid);
  if (next === null || next === first) return;
  await waitForMint(next, signal, Math.max(0, deadlineMs - Date.now()));
}

/**
 * One exchange for one app, shaped for {@link ensureAppToken}.
 *
 * The exchange goes through `sharedAuthFetch`, which carries the session
 * bearer because `APP_TOKEN_ROUTE` is a reserved platform prefix (C1 hop 1).
 * The server's own `expiresIn` becomes the store's lifetime, so the shell
 * never holds a token past the point the server agreed to (R-041).
 */
function mintAppTokenFor(appUuid: string): Promise<MintedAppToken> {
  return mintAppToken(appUuid, sharedAuthFetch).then((response) => ({
    accessToken: response.accessToken,
    expiresInSec: response.expiresIn,
  }));
}

/**
 * One JSON call to core-api through the shell's session fetch, `authFetch`.
 *
 * Two bounds cover the call (R-127). `authFetch` bounds the wait for the headers of each
 * attempt, except the maintenance retry of a request that is not safe to send again. The call owns one `createBodyDeadline` of {@link shellAttemptTimeoutMs}, whose signal
 * the request carries beside any caller signal. Its timer starts only after the headers are in,
 * so no cold-start or maintenance retry sees a spent signal. Every body read runs inside it:
 *
 * - A 2xx body that stalls rejects the call with a `TimeoutError`.
 * - A 410 or other non-2xx body that stalls reads as `null`, and the call throws the error the
 *   status gives.
 *
 * The call goes through the kernel guard: a `/v1/admin` path with the kernel origin on gets the
 * local 403 of {@link kernelOriginRefusal}, which throws an {@link ApiError} with status 403 and
 * the parsed refusal as `body`, and no request leaves (WS-16b). The path leaves as the absolute
 * URL it resolved to against `window.location.href` (R-125).
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const deadline = createBodyDeadline(attemptTimeoutMsInUse);
  const res = await guardedAuthFetch(readFetchTarget(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    signal: deadline.join(init?.signal),
  });
  // 410 + canonical DeprecationResponse body — the deprecation contract
  // (`cloud/platform-sdk/docs/api-deprecation-contract.md`). Route through
  // a typed error so `safeRequest()` can convert to the sentinel without
  // every caller knowing about the contract.
  if (res.status === RETIRED_STATUS) {
    const body = await deadline.read<unknown>(() => res.json()).catch(() => null);
    if (isDeprecationResponse(body)) {
      const arrow = body.replacement ? ` → ${body.replacement}` : '';
      console.warn(`[api-deprecation] ${path} retired: ${body.message}${arrow}`);
      throw new EndpointRetiredError(path, body);
    }
    // 410 without the canonical body — fall through to the generic
    // ApiError path so callers see a typed error rather than a silent
    // sentinel that misrepresents the server's intent.
    throw new ApiError(res.status, parseErrorMessage(body, res.statusText), body);
  }
  // 431 — Node's HTTP parser refused the request for the size of its own
  // headers, before core-api routed it. There is no JSON body to parse (the
  // generic arm below would fall back to `res.statusText`, "Request Header
  // Fields Too Large", which tells a reader nothing they can act on) and no
  // server-side log line keyed to this URL, so the console line is the only
  // record an operator gets from the browser. Same shape as the 410 arm above.
  if (res.status === REQUEST_HEADER_TOO_LARGE_STATUS) {
    console.error(
      `[api-session-too-large] ${path} refused with ${REQUEST_HEADER_TOO_LARGE_STATUS}: ` +
        'the Authorization header exceeds the server\'s limit. Not retryable.',
    );
    throw new ApiError(res.status, SESSION_TOO_LARGE_MESSAGE, null);
  }
  if (!res.ok) {
    const body = await deadline.read<unknown>(() => res.json()).catch(() => null);
    throw new ApiError(res.status, parseErrorMessage(body, res.statusText), body);
  }
  if (res.status === 204) return undefined as T;
  return deadline.read(() => res.json() as Promise<T>);
}

/**
 * Variant of `request()` that converts `EndpointRetiredError` into a
 * `RetiredEndpoint` sentinel return value. Hooks that consume potentially
 * deprecated endpoints should call `safeRequest()` and branch on
 * `'retired' in result`. All other errors propagate unchanged.
 *
 * ⛔ KEPT WITH NO CALLER, and the reason is not the admin extraction. This is the shell's half of
 * the api-deprecation contract (`@zion/sdk-contracts`' `DeprecationResponse` and
 * `@zion/sdk-nestjs`' `RetiredEndpoint` interceptor), the transport wrapper beside `request` and
 * `authedRequest` rather than an endpoint function. No core-api route emits 410 yet, so there is
 * nothing for it to serve until the first retirement. `__tests__/client-deprecation.test.ts` is
 * what keeps `request`'s 410 arms exercised, and it drives them through this wrapper. R-68(2)'s
 * caller-less sweep took the thirteen endpoint functions and left this one.
 */
export async function safeRequest<T>(
  path: string,
  init?: RequestInit,
): Promise<T | RetiredEndpoint> {
  try {
    return await request<T>(path, init);
  } catch (err) {
    if (err instanceof EndpointRetiredError) {
      return { retired: true, replacement: err.deprecation.replacement };
    }
    throw err;
  }
}

function authed(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function authedRequest<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(path, {
    ...init,
    headers: { ...authed(), ...init?.headers },
  });
}

/**
 * The one request header that selects cookie transport for a session mint.
 *
 * Every function below that mints a session spreads this into its `headers`. core-api's
 * `AccessCookieInterceptor` reads it, sets `zion_refresh_token` as an HttpOnly cookie from the
 * issued pair, and removes `refreshToken` from the JSON body, so the shell never holds a
 * script-readable refresh credential (decision D4, chain C2 step 2).
 *
 * @returns A fresh object whose one key is `SESSION_TRANSPORT_HEADER` and whose value is
 *   `SESSION_TRANSPORT_COOKIE`. Both come from `@zion/sdk-contracts`; neither is spelled here
 *   (default F8). A new object per call, so a caller's spread cannot mutate a shared one.
 *
 * It takes no argument, reads no state, and has no failure mode. It is exported so one unit
 * test pins the key and the value without a network round trip; the per-route proof that the
 * header reaches the wire is `__tests__/client-session-transport.test.ts` (S1 to S8).
 *
 * `@zion/sdk-react` declares a `sessionTransportHeaders()` of its own for the refresh and
 * logout calls it owns. Ruling R-14c keeps both homes and records the constant pair as the
 * shared thing.
 */
export function sessionTransportHeaders(): Record<typeof SESSION_TRANSPORT_HEADER, string> {
  return { [SESSION_TRANSPORT_HEADER]: SESSION_TRANSPORT_COOKIE };
}

// ── Public (unauthenticated) ─────────────────────────────────────────────────

export async function getProviders(): Promise<ProvidersResponse> {
  return request<ProvidersResponse>('/v1/auth/providers');
}

export async function login(data: LoginRequest): Promise<LoginResponse> {
  return request<LoginResponse>('/v1/auth/login', {
    method: 'POST',
    headers: { ...sessionTransportHeaders() },
    body: JSON.stringify(data),
  });
}

// `refreshTokens(refreshToken)` was REMOVED here by the DP session-cookie feature (D9, D12).
// `@zion/sdk-react` owns hydration and rotation, and its `withRefreshLock` is the single
// serializer of a single-use token; a second refresh from this client would race it and burn
// the token. The one shell caller was `ChannelSwitcher`, which now calls `refreshSession()`
// off `useAuth()`. `POST /v1/auth/refresh` and `POST /v1/auth/logout` leave this client
// entirely: the SDK is their only browser caller.

export async function requestPasswordReset(email: string): Promise<void> {
  return request<void>('/v1/auth/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * ⛔ Returns a FRESH TOKEN PAIR — do not discard it (#1137, F-3).
 *
 * `AuthService.confirmPasswordReset` revokes **every** session
 * (`revokeAllUserSessions`) and then issues a new pair, exactly like
 * `password-change` does. This function typed the response `void`, so the pair
 * went on the floor: any tab already signed in was silently dead on its next
 * request, and a user resetting from the email link had to log in again by hand
 * after the server had already authenticated them.
 *
 * Found by `__tests__/core-api-body-parity.test.ts`, which compares the handler's
 * awaited return type against this type argument. It is the SAME defect as the
 * hand-found `password-change` one, on a second route — which is why the gate
 * exists rather than a fifth hand inspection.
 */
export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<LoginResponse> {
  return request<LoginResponse>('/v1/auth/password-reset/confirm', {
    method: 'POST',
    headers: { ...sessionTransportHeaders() },
    body: JSON.stringify({ token, newPassword }),
  });
}

export async function verifyEmail(token: string): Promise<void> {
  return request<void>('/v1/auth/email/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function getPasskeyAuthOptions(): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>('/v1/auth/passkey/login/options', {
    method: 'POST',
  });
}

export async function verifyPasskeyAuth(credential: unknown): Promise<LoginResponse> {
  // Backend's `verifyAuthentication` body is `{ orgSlug?, response }`. The
  // shell historically sent `{ credential }` here — the controller's
  // `body.response as never` cast papered over the mismatch and the
  // service crashed at `response.id` because `body.response` was
  // undefined. Send the field as `response` so it matches both the
  // backend DTO and the existing `verifyPasskeyRegistration` shape in
  // home-api.ts.
  return request<LoginResponse>('/v1/auth/passkey/login/verify', {
    method: 'POST',
    headers: { ...sessionTransportHeaders() },
    body: JSON.stringify({ response: credential }),
  });
}

/**
 * The IdP authorize URL for `provider`. `nonce` is the per-flow marker the caller minted
 * (R-108): core-api requires it as the `nonce` query parameter (32 lower-case hex characters)
 * and answers 400 without it.
 */
export async function getOAuthUrl(provider: string, nonce: string): Promise<{ url: string }> {
  const query = new URLSearchParams({ nonce }).toString();
  return request<{ url: string }>(`/v1/auth/oauth/${provider}/url?${query}`);
}

// The route is `/v1/invitations/accept` — it does NOT sit under the `v1/auth`
// prefix, which is where this client wrongly looked for it for months. core-api
// declares it as a full path on a bare `@Controller()`
// (`admin/admin-invitations.controller.ts` → `@Post('v1/invitations/accept')`),
// and there is no `v1/auth` invitations controller at all. Verified against the
// server route set by `__tests__/core-api-route-parity.test.ts`, which fails the
// build on a drift.
//
// ⛔ The wrong path is described here rather than written out, deliberately.
// A text-based drift scanner read this file's comments on purpose, so spelling
// the bad path out made it report the path as an endpoint this client uses.
// That scanner left the tree with the `zion-dev` launcher on 2026-10-02; the
// path stays described, not written, so a future text scanner cannot misread it.
//
// ⛔ `displayName` is REQUIRED, not optional. The server schema is
// `z.object({ token, displayName, password })` with all three required
// (`admin-invitations.controller.ts`), and `displayName` becomes the new user's
// `users.display_name`. It was previously absent here AND absent from the form, so
// once the 404 above was corrected the call would have 400'd instead — a defect
// that merely looked addressed. Path parity does not imply body parity: the
// route-parity gate checks the path and method only.
export async function acceptInvitation(
  token: string,
  data: { displayName: string; password: string },
): Promise<LoginResponse> {
  return request<LoginResponse>(`/v1/invitations/accept`, {
    method: 'POST',
    headers: { ...sessionTransportHeaders() },
    body: JSON.stringify({ token, ...data }),
  });
}

export async function exchangeOAuthCode(code: string): Promise<LoginResponse> {
  return request<LoginResponse>('/v1/auth/exchange', {
    method: 'POST',
    headers: { ...sessionTransportHeaders() },
    body: JSON.stringify({ code }),
  });
}

// ── Authenticated ────────────────────────────────────────────────────────────

export async function getMe(): Promise<MyProfile> {
  return authedRequest<MyProfile>('/v1/auth/me');
}

/**
 * Project a wire-format app entry into the `AppRegistryEntry` shape the
 * shell's render code expects.
 *
 * Background — contract drift, 2026-05-19:
 *
 *   The shell's `AppRegistryEntry.manifest` interface (see types.ts) is
 *   the LEGACY flat shape that pre-dates the v3 app-manifest schema
 *   tightening:
 *     `{ title, icon, mountPath, defaultRoute, category, navigation }`
 *
 *   The current SDK app-manifest schema
 *   (`@zion/sdk-contracts/schemas/app-manifest.schema.json`) is:
 *     - top-level: `displayName`, `icon`, `category`
 *     - `shell: [{ mountPath, defaultRoute, category, uiPackage }]`
 *       (array; multi-route apps have multiple entries)
 *
 *   The release-engine and DP core-api both store + serve the manifest
 *   in the NEW shape. The shell's render code (Header.tsx,
 *   AppHost.tsx, dashboard-utils.ts, app-loader-utils.ts, nav-utils.ts)
 *   still reads `manifest.title` / `manifest.mountPath` etc., which
 *   are undefined in the new shape — visible symptom: app appears
 *   in the nav as an unlabeled icon, clicking does nothing (route is
 *   `undefined`). Filed against sdk-docs 0.4.0 on dp-app GKE.
 *
 *   This projection bridges the two shapes at the API boundary:
 *     - `manifest.displayName` (new) → `manifest.title` (legacy)
 *     - the row's routed surface's `mountPath` and `defaultRoute` →
 *       `manifest.mountPath` and `manifest.defaultRoute`. Since stage 5b
 *       a `shell[]`-only app arrives with one routed surface, `legacy.v1`,
 *       which core-api builds from `shell[0]` (R-129), so `shell[0]` no
 *       longer outranks a declared surface
 *     - `manifest.productArea` → `manifest.category`, preferred over the
 *       shell entry, trimmed and clamped to 64 (C-025 / C-037)
 *     - `manifest.shell[0].category` → `manifest.category` when no
 *       productArea is declared (with fallback to top-level
 *       `manifest.category` for apps that don't declare per-shell-entry
 *       categories)
 *
 *   `shell[]` itself is preserved so downstream "suite" rendering
 *   (which already reads `manifest.shell[]`) keeps working. The
 *   `navigation` field is not in the new schema — set to `[]` so the
 *   render code's `.map()` calls don't NPE; apps that need a static
 *   nav declaration will need a separate path.
 *
 *   This is intentionally a one-line API-boundary translation, not a
 *   sweeping rename of every consumer. The proper fix is to migrate
 *   the shell's render code to the new schema fields and retire the
 *   legacy projection — that's a bigger refactor for a follow-up.
 */
/**
 * Type-narrow a raw JSON object to a `ShellEntryDeclaration`. The new
 * v3 schema requires `mountPath` + `defaultRoute` to be strings — anything
 * else is silently dropped from the projection (the entry is structurally
 * invalid and would crash the render code's `.mountPath` reads).
 */
function isShellEntry(x: unknown): x is ShellEntryDeclaration {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.mountPath === 'string' && typeof o.defaultRoute === 'string';
}

/**
 * The launcher mount of a row (R-25 b, R-129): the first routed, non-root surface's `mountPath`
 * and its declared `defaultRoute`, else `'/'`. That surface is `legacy.v1` for an app that
 * declares only `shell[]`. A row with no such surface (an embedded-only row, a root-only row, no
 * surfaces) reads `{}` and projects `''`. In `projectAppManifest` only the legacy flat
 * `mountPath` and `defaultRoute` fields rank ahead of it; a `shell[0]` entry never does (R-129).
 * `surfaces` is the row's `projectSurfaces` result, so the block is parsed once. Pure.
 */
function routedSurfaceMount(surfaces: AppRegistryEntry['surfaces']): Partial<ShellEntryDeclaration> {
  const routed = surfaces.find((decl) => decl.kind === 'routed' && decl.root !== true && typeof decl.mountPath === 'string');
  return routed?.mountPath === undefined ? {} : { mountPath: routed.mountPath, defaultRoute: routed.defaultRoute ?? '/' };
}

/**
 * `outerAppId` is the wire row's own `ResolvedApp.appId`. `surfaces` is the row's parsed
 * `surfaces[]` (`projectSurfaces`), which gives `mountPath` and `defaultRoute`; a `shell[0]` entry
 * gives only the `category` fallback and the `shell` list (R-129). `ResolvedApp.manifest` is nullable
 * (`fixture-resolution.service.ts`, `manifest: Record<string, unknown> | null`), and a standalone
 * row arrives with `null`, so without it the projected `title` is `''` and every consumer that
 * reads `manifest.title` renders a blank label. The projection is the one boundary that resolves
 * the title; consumers carry no fallback of their own.
 */
function projectAppManifest(
  raw: unknown,
  outerAppId?: unknown,
  surfaces: AppRegistryEntry['surfaces'] = [],
): AppRegistryEntry['manifest'] {
  const m = (raw ?? {}) as Record<string, unknown>;
  const shellArr: ShellEntryDeclaration[] = Array.isArray(m.shell)
    ? m.shell.filter(isShellEntry)
    : [];
  const primaryCategory = shellArr[0]?.category;
  const surfaceMount = routedSurfaceMount(surfaces);
  const title =
    (typeof m.displayName === 'string' && m.displayName) ||
    (typeof m.title === 'string' && m.title) ||
    (typeof m.appId === 'string' && m.appId) ||
    (typeof outerAppId === 'string' && outerAppId) ||
    '';
  return {
    appId: typeof m.appId === 'string' ? m.appId : '',
    title,
    icon: typeof m.icon === 'string' ? m.icon : '',
    mountPath: typeof m.mountPath === 'string' ? m.mountPath : (surfaceMount.mountPath ?? ''),
    defaultRoute: typeof m.defaultRoute === 'string' ? m.defaultRoute : (surfaceMount.defaultRoute ?? '/'),
    // ⛔ The clamp counts CODE POINTS, because the publish door's `maxLength`
    // does. Ajv measures `maxLength` in code points; `String.prototype.slice`
    // counts UTF-16 units, so the retired `.slice(0, 64)` spelling cut a
    // 64-emoji productArea the door had ACCEPTED down to 32, and cut 63 ASCII
    // plus one emoji mid-pair, leaving a lone high surrogate 0xd83d that renders
    // as a replacement glyph in the heading and the app-switcher label (both
    // measured, v-stageA-sec NIT-1). Spreading into an array iterates code
    // points, so this never splits a pair and the two ends agree about what
    // "64" counts. The bound is unchanged for any all-ASCII value.
    category: [
      ...(
        typeof m.productArea === 'string' && m.productArea.trim() !== ''
          ? m.productArea
          : typeof primaryCategory === 'string'
            ? primaryCategory
            : (typeof m.category === 'string' ? m.category : '')
      ).trim(),
    ]
      .slice(0, 64)
      .join(''),
    description: typeof m.description === 'string' ? m.description : undefined,
    navigation: Array.isArray(m.navigation)
      ? (m.navigation as AppRegistryEntry['manifest']['navigation'])
      : [],
    shell: shellArr,
  };
}

/**
 * The row's declared surfaces from `ResolvedApp.surfaces` (layered shell surfaces, WS-5 §3.7,
 * WS-6 §3.9), parsed with the cp-dp wire twin for the row's own app:
 * `surfaceDeclarationsSchemaForApp(appId)` (#3673, admin-redesign-asks C-2, D-10).
 *
 * `appId` is the wire row's `ResolvedApp.appId`, the registered app slug, never the manifest's
 * own `appId` field. A value that is not a string reads as `''`, which no first-party row names.
 *
 * - A first-party slug (a row of `FIRST_PARTY_APPS`) may declare a surface at its one listed
 *   mount path, `/admin` for `admin-app`, which begins with a reserved segment.
 * - Every other slug parses under the general rule, so a third-party row at `/admin` is refused.
 *
 * An absent field, a legacy app and a block the twin refuses (an unknown key, a schema outside
 * the type language, a reserved `mountPath` the app does not own, more than
 * `MAX_SURFACES_PER_APP` rows) all read `[]`, which closes that row's root and routed surfaces:
 * the fail-closed direction. Pure.
 */
function projectSurfaces(raw: unknown, appId: unknown): AppRegistryEntry['surfaces'] {
  const parsed = surfaceDeclarationsSchemaForApp(typeof appId === 'string' ? appId : '').safeParse(raw);
  return parsed.success ? parsed.data : [];
}

/** The one `AppBundle` the wire entry `value` carries, or `null` when the shape check refuses it. Pure. */
function bundleEntryOf(value: unknown): AppBundle | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const keys = Object.keys(value);
  if (keys.length !== 2 || !keys.includes('bundleUrl') || !keys.includes('cssUrl')) return null;
  const { bundleUrl, cssUrl } = value as Record<string, unknown>;
  if (typeof bundleUrl !== 'string' || bundleUrl === '') return null;
  if (cssUrl !== null && (typeof cssUrl !== 'string' || cssUrl === '')) return null;
  return Object.freeze({ bundleUrl, cssUrl });
}

/**
 * The row's bundles from `ResolvedApp.bundles` (layered shell surfaces, R-1, R-102): one entry per
 * UI package, keyed by the `uiPackage` a surface declaration names. The wire value is untrusted,
 * so the check is strict, as the `.strict()` surfaces twin is:
 *
 * - A value that is not a plain object (absent, `null`, an array, a string) reads `{}`.
 * - An entry is kept when its key is a non-empty string and its value carries exactly the two
 *   keys `bundleUrl`, a non-empty string, and `cssUrl`, a non-empty string or `null`. Every other
 *   entry is dropped, so the surfaces that name that package have no bundle: the fail-closed
 *   direction.
 *
 * The answer is a frozen plain object of own keys. A reader looks a package up with
 * `Object.hasOwn`, so a key such as `constructor` never reads `Object.prototype`. This function
 * checks the shape only; the loader host checks each URL's origin before any import. Pure.
 */
function projectBundles(raw: unknown): NonNullable<AppRegistryEntry['bundles']> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return Object.freeze({});
  const kept: Array<[string, AppBundle]> = [];
  for (const [uiPackage, value] of Object.entries(raw)) {
    const bundle = bundleEntryOf(value);
    if (uiPackage !== '' && bundle !== null) kept.push([uiPackage, bundle]);
  }
  return Object.freeze(Object.fromEntries(kept));
}

/**
 * The wire keys of a `GET /v1/apps` row that a projected row never carries (R-129):
 * `ResolvedApp.bundleUrl` and `.cssUrl`, the owner fixture's own pair, and a `bundleCount` a wire
 * row may hold. The loader resolves every surface through `bundles[uiPackage]` alone, so no
 * reader of the registry can load a bundle through a row-level URL.
 */
const ROW_BUNDLE_KEYS: ReadonlySet<string> = new Set(['bundleUrl', 'cssUrl', 'bundleCount']);

/**
 * A copy of the wire row `entry` with the own keys in {@link ROW_BUNDLE_KEYS} left out. Every other
 * own key keeps its value, a JSON-parsed own `__proto__` key included (`Object.fromEntries`
 * defines own properties). Does not change `entry`. Pure.
 */
function withoutRowBundle(entry: Readonly<Record<string, unknown>>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(entry).filter(([key]) => !ROW_BUNDLE_KEYS.has(key)));
}

/** How many `getApps` reads this module has sent; each read takes the next number. */
let appsReadsIssued = 0;
/**
 * The number of the newest read that settled and claimed the registry snapshot; 0 before the
 * first. A success claims it and records its rows; a rejection claims it and records no rows.
 */
let appsReadRecorded = 0;

/**
 * `GET /v1/apps`, projected. A successful answer is also recorded as the app registry snapshot
 * (`recordAppRegistry`) before the promise resolves, so the loader host resolves the rows the
 * caller renders. A rejection records no rows and reaches the caller unchanged.
 *
 * Reads are numbered in the order they are sent. An answer is recorded only when no read sent
 * after it has settled already, as a success or as a rejection. So when two reads overlap (a token
 * refresh during a slow read) and the older one answers last, the snapshot keeps the newer rows,
 * the rows the pane shows, and a version pinned between the two reads never mounts at the old
 * `bundleUrl` (rejudge-2). A newer read that rejects claims its number too. The caller that
 * clears the snapshot on a rejection (`boot.ts`, `forgetAppRegistry`) then keeps it clear: an
 * older answer that lands later records nothing, so no stale row, bundle or permission key from
 * another session returns. Every caller still receives its own read's rows.
 */
export async function getApps(): Promise<AppRegistryEntry[]> {
  appsReadsIssued += 1;
  const issue = appsReadsIssued;
  let raw: Array<Record<string, unknown>>;
  try {
    raw = await authedRequest<Array<Record<string, unknown>>>('/v1/apps');
  } catch (err) {
    if (issue > appsReadRecorded) appsReadRecorded = issue;
    throw err;
  }
  const apps = projectApps(raw);
  if (issue > appsReadRecorded) {
    appsReadRecorded = issue;
    recordAppRegistry(apps);
  }
  return apps;
}

function projectApps(raw: ReadonlyArray<Record<string, unknown>>): AppRegistryEntry[] {
  return raw.map((wire) => {
    const entry = withoutRowBundle(wire);
    return {
      ...(entry as unknown as AppRegistryEntry),
      // `ResolvedApp.channelName` is required on the wire, but project it
      // defensively so a malformed entry can never make it `undefined` (the
      // home version chip reads `.channelName` directly).
      channelName: typeof entry.channelName === 'string' ? entry.channelName : '',
      // The catalog id the app-token exchange takes as its `appId` (R-006). It
      // is already on the wire as `ResolvedApp.appUuid`; until now the cast
      // above dropped it. `null` for a row the catalog does not back.
      appUuid: typeof entry.appUuid === 'string' ? entry.appUuid : null,
      // The keys this principal holds for this app (D-6, D-12). `permissionKeysOf`
      // is the one boundary that turns an absent or malformed wire field into
      // `[]`, so every reader downstream handles one shape and a malformed
      // field can only close the app's UI gate.
      permissionKeys: permissionKeysOf(entry),
      // One bundle per UI package (R-102); the loader resolves a declared surface through it alone.
      // `withoutRowBundle` left the wire row's own `bundleUrl` and `cssUrl` out (R-129); only the
      // roll-window arm reads them from `wire`, as a map entry (R-136, R-137).
      ...withSurfaces(wire, entry),
    };
  });
}

/**
 * The name of the one routed surface core-api gives an app that declares only `shell[]` (R-129).
 * {@link rollLegacyOf} builds the same surface for a row from an older core-api.
 */
const LEGACY_SURFACE_NAME = 'legacy.v1';

/**
 * `value[key]` when `value` is a non-null object that holds `key` as an own property and its value
 * is a string, else `null`. An inherited property never counts, so a JSON-parsed own `__proto__`
 * key or a prototype chain cannot supply a value. Pure.
 */
function ownStringOf(value: unknown, key: string): string | null {
  if (typeof value !== 'object' || value === null || !Object.hasOwn(value, key)) return null;
  const field: unknown = Reflect.get(value, key);
  return typeof field === 'string' ? field : null;
}

/** The own property `key` of `value` when `value` is a non-null object, else `undefined`. Pure. */
function ownFieldOf(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null && Object.hasOwn(value, key) ? Reflect.get(value, key) : undefined;
}

/**
 * The owner UI fixture's id of a wire row, as core-api picks the owner fixture for the row's own
 * `bundleUrl` and `cssUrl` (`FixtureResolutionService.resolveApps`): the first entry of the own
 * `fixturesArray` whose own `isOwnerVersion` is `true` and whose own `bundleUrl` is a string, else
 * the first entry whose own `bundleUrl` is a string. The answer is that entry's own string
 * `fixtureName`, or `null` when `fixturesArray` is absent or not an array, when no entry carries a
 * string `bundleUrl`, or when the picked entry holds no string `fixtureName`. Inherited properties
 * never count. Pure.
 */
function ownerFixtureNameOf(wire: Record<string, unknown>): string | null {
  const fixtures = ownFieldOf(wire, 'fixturesArray');
  if (!Array.isArray(fixtures)) return null;
  const withBundle = fixtures.filter((fixture: unknown) => ownStringOf(fixture, 'bundleUrl') !== null);
  const owner: unknown = withBundle.find((fixture: unknown) => ownFieldOf(fixture, 'isOwnerVersion') === true) ?? withBundle[0];
  return ownStringOf(owner, 'fixtureName');
}

/**
 * The surfaces and bundles of a wire row that a core-api from before R-129 served, during the DP
 * roll (WS-52b-R1, WS-52b-R2, WS-52b-R6, WS-52b-R8). `bin/release-dp` rolls the shell and core-api
 * Deployments together, so a new shell pod can read `GET /v1/apps` from an older core-api pod.
 * That pod gives a `shell[]`-only app no surface, and keys `bundles` by the fixture manifest alone.
 * Without this arm the app has no mount path, or its `legacy.v1` surface has no bundle, and its
 * pane fails.
 *
 * Inputs: `wire`, the wire row with its own `bundleUrl` and `cssUrl`, and `bundles`, the row's
 * projected map. The arm builds the `legacy.v1` surface and the bundle entry that core-api's
 * `resolvedAppSurfacesOf` builds (R-129, R-136), with the same per-app parser,
 * `surfaceDeclarationsSchemaForApp(appId)`. Output per input class:
 * 1. The wire row's own `surfaces` is present and is not an empty array: no surface. A non-empty
 *    wire block that the twin refuses stays `[]` (the fail-closed direction).
 * 2. The app manifest declares a surface (a non-empty own `surfaces` array): no surface.
 * 3. Else the candidate takes `mountPath`, `uiPackage` and `defaultRoute`, own string fields of
 *    the first entry of the manifest's own `shell` array. A later entry never counts. When that
 *    entry names no string `uiPackage` and the row has an owner bundle (below), the candidate takes
 *    `'@' + ownerFixtureNameOf(wire)` (R-136). The candidate must pass the parser, so a
 *    third-party app at a reserved first segment, a `uiPackage` with no leading `@` and a
 *    `defaultRoute` with no leading `/` all give no surface.
 * 4. With a surface, its `uiPackage` holds no own key of the wire `bundles` map, and the row has
 *    an owner bundle: the map gains one entry, the owner bundle under that `uiPackage`. A wire key
 *    that the shape check refused still counts as present, so the arm never replaces it.
 *
 * The owner bundle is the wire row's own `{ bundleUrl, cssUrl }` when it passes the same shape
 * check as a map entry; core-api takes that pair from the owner fixture. Without it, the arm keys
 * no entry and takes no `uiPackage` fallback, as core-api does for an app with no UI owner.
 *
 * Every class with no surface returns `bundles` as it came. A current core-api already serves the
 * surface and the entry for every row that passes 2 to 4, so the arm changes no row it serves.
 * The loader still resolves every surface through `bundles[uiPackage]` alone, and the projected row
 * still carries no row-level URL (R-129). Pure; the returned map is frozen.
 */
function rollLegacyOf(
  wire: Record<string, unknown>,
  bundles: NonNullable<AppRegistryEntry['bundles']>,
): Pick<AppRegistryEntry, 'surfaces'> & { bundles: NonNullable<AppRegistryEntry['bundles']> } {
  const none = { surfaces: [], bundles };
  const wireSurfaces = ownFieldOf(wire, 'surfaces');
  if (wireSurfaces !== undefined && !(Array.isArray(wireSurfaces) && wireSurfaces.length === 0)) return none;
  const manifest = ownFieldOf(wire, 'manifest');
  const declared = ownFieldOf(manifest, 'surfaces');
  if (Array.isArray(declared) && declared.length > 0) return none;
  const shell = ownFieldOf(manifest, 'shell');
  if (!Array.isArray(shell)) return none;
  const first: unknown = shell[0];
  const ownerBundle = bundleEntryOf({ bundleUrl: ownFieldOf(wire, 'bundleUrl'), cssUrl: ownFieldOf(wire, 'cssUrl') });
  const ownerName = ownerBundle === null ? null : ownerFixtureNameOf(wire);
  const mountPath = ownStringOf(first, 'mountPath');
  const uiPackage = ownStringOf(first, 'uiPackage') ?? (ownerName === null ? null : `@${ownerName}`);
  const defaultRoute = ownStringOf(first, 'defaultRoute');
  if (mountPath === null || uiPackage === null || defaultRoute === null) return none;
  const surfaces = projectSurfaces([{ name: LEGACY_SURFACE_NAME, kind: 'routed', uiPackage, mountPath, defaultRoute }], wire.appId);
  const [legacy] = surfaces;
  if (legacy === undefined) return none;
  if (ownerBundle === null || ownFieldOf(ownFieldOf(wire, 'bundles'), legacy.uiPackage) !== undefined) return { surfaces, bundles };
  return { surfaces, bundles: Object.freeze({ ...bundles, [legacy.uiPackage]: ownerBundle }) };
}

/**
 * The row's parsed surfaces, its bundles and the manifest projected from them, so `surfaces[]` is
 * parsed once. `entry` is `wire` with no row-level bundle key. A row with no parsed surface takes
 * {@link rollLegacyOf}, the arm for an older core-api, which alone reads `wire`'s own pair.
 */
function withSurfaces(
  wire: Record<string, unknown>,
  entry: Record<string, unknown>,
): Pick<AppRegistryEntry, 'surfaces' | 'manifest'> & { bundles: NonNullable<AppRegistryEntry['bundles']> } {
  const bundles = projectBundles(entry.bundles);
  const parsed = projectSurfaces(entry.surfaces, entry.appId);
  const projected = parsed.length > 0 ? { surfaces: parsed, bundles } : rollLegacyOf(wire, bundles);
  return { ...projected, manifest: projectAppManifest(entry.manifest, entry.appId, projected.surfaces) };
}

// Exported for unit-test coverage of the projection table only — the
// rest of the codebase consumes `getApps()` and sees the projected shape.
export const __test__projectAppManifest = projectAppManifest;

// ── User Preferences ────────────────────────────────────────────────────────

export async function getPreferences(appId: string): Promise<Record<string, unknown>> {
  return authedRequest<Record<string, unknown>>(`/v1/auth/preferences/${appId}`);
}

export async function updatePreferences(appId: string, prefs: Record<string, unknown>): Promise<Record<string, unknown>> {
  return authedRequest<Record<string, unknown>>(`/v1/auth/preferences/${appId}`, {
    method: 'PATCH',
    body: JSON.stringify(prefs),
  });
}

// ── Admin: Users and Groups ─────────────────────────────────────────────────
//
// The user and group reads and the group write left this file in the S5 cutover (R-68(2)): their
// only production callers were the deleted built-in `UsersPage` and `GroupsPage`, and the hosted
// admin app calls `/v1/admin/users` and `/v1/admin/groups` through its own client.
// `AdminController` still registers every route and `ADMIN_CONTRACT_ROUTES` still censuses them,
// so this is a client-side deletion and not a route retirement.
// `__tests__/core-api-route-parity.test.ts` is what keeps a client function and a route in step.

// ── Admin: Org Settings ──────────────────────────────────────────────────────
//
// The org settings read (`GET /v1/admin/org`) left this file in kernel apps WS-16b (design
// section 3.8, OQ-5). Its one caller was the home hero, which now shows a neutral label. The
// route is a kernel route, and the shell sends no session bearer to `/v1/admin` with the kernel
// origin on.

// ── Admin: Release Channels ─────────────────────────────────────────────────
//
// Every channel read, the two channel writes, the two assignment calls and the canary start left
// this file in the S5 cutover (R-68(2)): every caller was a built-in admin page, and the hosted
// admin app calls them through its own client. `ChannelController` and
// `CustomerChannelsController` still register the routes and they are still in
// `cloud/dp/core-api/openapi.json`, so this is a client-side deletion and not a route retirement.
// `__tests__/core-api-route-parity.test.ts` is what keeps a client function and a route in step,
// so it is the gate to consult before adding or deleting either.
//
// ⛔ THE TWO CHANNEL PREFIXES ARE DIFFERENT CONTROLLERS. `v1/release/customer-channels` and
// `v1/release/channels` both exist in core-api (`customer-channels.controller.ts` and
// `channel.controller.ts`) and differ by one segment. The route-parity gate notices a slip only
// after the fact, so read the prefix rather than pattern-match on the neighbours.
//
// ⛔ `appId` IS THE `release.apps_catalog` UUID, NOT A SLUG (§10.2a). The server binds it with a
// uuid `@Param({ schema })`, so a slug is a 400 before any query.

// ── Per-user channel selection (self-service, #1075) ────────────────────────
// Self-scoped: the endpoints derive the caller from the JWT `sub` — the client
// never sends a userId. `getMyChannels` lists the channels the caller is
// entitled to consume (group-allowed + floor-passing), flagging the one the
// resolver currently mints. `setMyChannel` persists the preference server-side
// (validated inside the resolver against the same allowed pool); `null` clears
// it and reverts to auto-resolution.

export async function getMyChannels(): Promise<MyChannelsResponse> {
  return authedRequest<MyChannelsResponse>('/v1/release/my-channels');
}

export async function setMyChannel(channelId: string | null): Promise<MyChannelsResponse> {
  return authedRequest<MyChannelsResponse>('/v1/release/my-channel', {
    method: 'PUT',
    body: JSON.stringify({ channelId }),
  });
}

// ── Admin: Auth Providers ────────────────────────────────────────────────────

// ── Admin: Recovery Codes ───────────────────────────────────────────────────

// ── Admin: SCIM Tokens ───────────────────────────────────────────────────────

// ── Admin: OAuth Clients ─────────────────────────────────────────────────────
// Static org-scoped public-client registry (AD-7, plan 2026-06-12-dp-oauth-
// pkce-authorization-server). Disable rides update (`{disabled: true}`).

// ── Admin: DCR redirect hosts ────────────────────────────────────────────────
// The per-org RFC 7591 redirect-host allowlist (plan 2026-08-04-dp-dcr-dynamic-
// permissions, D-2) — the load-bearing control on an UNAUTHENTICATED create
// endpoint. `settings:manage` on every verb.
//
// ⚠️ `host` is IMMUTABLE and therefore absent from the update payload: editing
// it in place would leave `createdByUserId` naming the admin who approved the
// OLD value. The UI offers delete-and-re-create instead.

// ── OAuth consent (delegated-authorization flow, AD-2) ──────────────────────
// The consent endpoints answer with RFC 6749 `{error, error_description}`
// envelopes (core-api's oauth-error filter), and OAuthConsentPage branches
// on the error CODE (e.g. `invalid_scope`) — so these three functions throw
// `OAuthFlowError` carrying both the status and the RFC code instead of the
// generic ApiError.

/** Error from the OAuth consent endpoints, carrying the RFC 6749 code. */
export class OAuthFlowError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly oauthCode: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'OAuthFlowError';
  }
}

/**
 * One consent call through the shell's session fetch. Its two bounds are the ones of
 * {@link request}: `authFetch` bounds the headers of each attempt, and one `createBodyDeadline`
 * of {@link shellAttemptTimeoutMs} bounds the body read after them. A 2xx body that stalls
 * rejects with a `TimeoutError`. A non-2xx body that stalls reads as `null`, so the call throws
 * {@link OAuthFlowError} with the status and a null code. It goes through the same kernel guard
 * as {@link request}.
 */
async function oauthFlowRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const deadline = createBodyDeadline(attemptTimeoutMsInUse);
  const res = await guardedAuthFetch(readFetchTarget(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authed(),
      ...init?.headers,
    },
    signal: deadline.join(init?.signal),
  });
  // SCOPED IN with `request()`'s arm, not left out. `oauthFlowRequest` sends
  // the same bearer through `authed()`, so the consent page 431s for exactly
  // the readers the hero does, and its generic arm renders "Could not load the
  // authorization request." beside a Retry button that re-sends the same
  // oversized header. There is no RFC 6749 envelope on a 431 — Node refuses it
  // before core-api's oauth-error filter exists — so the code is null and the
  // status is the whole signal.
  if (res.status === REQUEST_HEADER_TOO_LARGE_STATUS) {
    console.error(
      `[api-session-too-large] ${path} refused with ${REQUEST_HEADER_TOO_LARGE_STATUS}: ` +
        'the Authorization header exceeds the server\'s limit. Not retryable.',
    );
    throw new OAuthFlowError(res.status, null, SESSION_TOO_LARGE_MESSAGE);
  }
  if (!res.ok) {
    const body: unknown = await deadline.read<unknown>(() => res.json()).catch(() => null);
    throw new OAuthFlowError(
      res.status,
      extractOAuthErrorCode(body),
      parseErrorMessage(body, res.statusText),
    );
  }
  return deadline.read(() => res.json() as Promise<T>);
}

export async function getAuthorizeRequest(id: string): Promise<AuthorizeRequestView> {
  return oauthFlowRequest<AuthorizeRequestView>(
    `/v1/auth/oauth/authorize/${encodeURIComponent(id)}`,
  );
}

/**
 * Approve a pending authorization request. `channelId` is the release channel
 * the user picked on the consent page (#1142, seam S1): it is optional, must be
 * an id the caller's `GET /v1/release/my-channels` listed, and is validated
 * server-side against the caller's entitled, floor-passing pool. Omitting it
 * reproduces the pre-#1142 wire exactly (`{}`) and lets the server auto-resolve.
 */
export async function approveAuthorizeRequest(
  id: string,
  channelId?: string,
): Promise<{ redirectTo: string }> {
  return oauthFlowRequest<{ redirectTo: string }>(
    `/v1/auth/oauth/authorize/${encodeURIComponent(id)}/approve`,
    { method: 'POST', body: JSON.stringify(channelId ? { channelId } : {}) },
  );
}

export async function denyAuthorizeRequest(id: string): Promise<{ redirectTo: string }> {
  return oauthFlowRequest<{ redirectTo: string }>(
    `/v1/auth/oauth/authorize/${encodeURIComponent(id)}/deny`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

// ── Admin: Platform Status ───────────────────────────────────────────────────

export async function getPlatformStatus(): Promise<PlatformStatus> {
  return authedRequest<PlatformStatus>('/v1/platform/status');
}

// ── Admin: Audit Log ─────────────────────────────────────────────────────────

// ── Admin: Permissions ──────────────────────────────────────────────────────

// ── Admin: Custom Roles ─────────────────────────────────────────────────────

// ── Admin: Per-role per-app permission grants ───────────────────────────────

export interface RoleAppPermissionGrant {
  orgRoleId: string;
  appId: string;
  permissionSlug: string;
  grantedAt: string;
  grantedBy: string | null;
}

// ── Admin: App Version Overrides ───────────────────────────────────────────
//
// The server calls these FIXTURE overrides, and the route paths say so:
// `AdminOverridesController` is `@Controller('v1/admin/fixtures/overrides')` and
// `AdminEffectiveFixturesController` is `@Controller('v1/admin/effective-fixtures')`
// (`core-api/src/admin/admin-overrides.controller.ts`). The `apps/overrides` and
// `effective-apps` paths these functions used to request are registered by no
// controller. Route parity is enforced by
// `__tests__/core-api-route-parity.test.ts`.

// ── Admin: Sessions ─────────────────────────────────────────────────────────

// ── Admin: Org Archive ──────────────────────────────────────────────────────

// ── Setup Wizard (public) ──────────────────────────────────────────────────

export async function getSetupStatus(): Promise<SetupStatus> {
  return request<SetupStatus>('/v1/setup/status');
}

export async function verifySetupToken(token: string): Promise<SetupTokenVerifyResponse> {
  return request<SetupTokenVerifyResponse>('/v1/setup/verify-token', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function getSetupCatalog(setupToken: string): Promise<ProviderCatalogEntry[]> {
  return request<ProviderCatalogEntry[]>('/v1/setup/auth-providers/catalog', {
    headers: { Authorization: `Bearer ${setupToken}` },
  });
}

export async function testSetupProvider(setupToken: string, data: TestProviderRequest): Promise<TestProviderResponse> {
  return request<TestProviderResponse>('/v1/setup/test-provider', {
    method: 'POST',
    headers: { Authorization: `Bearer ${setupToken}` },
    body: JSON.stringify(data),
  });
}

export async function completeSetup(setupToken: string, data: SetupCompleteDto): Promise<SetupCompleteResponse> {
  return request<SetupCompleteResponse>('/v1/setup/complete', {
    method: 'POST',
    headers: { Authorization: `Bearer ${setupToken}`, ...sessionTransportHeaders() },
    body: JSON.stringify(data),
  });
}

// ── Recovery Login (public) ────────────────────────────────────────────────

/**
 * Recovery-code login. The one mint route whose body carries a field outside the session
 * contract: `redirectTo`, the admin deep link the operator lands on
 * (`AuthController.recoveryLogin`). The caller destructures it off before the rest reaches
 * `AuthProvider.login()`, whose `sessionResponseSchema` parse is `.strict()` (R-21).
 */
export async function recoveryLogin(data: RecoveryLoginRequest): Promise<RecoveryLoginResponse> {
  return request<RecoveryLoginResponse>('/v1/auth/recovery-login', {
    method: 'POST',
    headers: { ...sessionTransportHeaders() },
    body: JSON.stringify(data),
  });
}

// ── Admin: Apps Catalog ─────────────────────────────────────────────────────

// ── Admin: Notifications ────────────────────────────────────────────────────

// ── Admin: Envelope errors (degraded apps banner — plan §D.3 / F3) ─────────

// ── Rings + Minimum-ring policy (read-only on DP) ──────────────────────────
//
// `GET /v1/release/rings` and `GET /v1/admin/minimum-ring` left this file in the S5 cutover
// (R-68(2)). Both are read by the hosted admin app's own client now, and the rolling-upgrade
// note moved with them: core-api answered 404 for an uncapped org until 2026-08-09 (#1466) and
// now answers 200 with `{ minimumRing: null, … }`, so whichever client reads the route must
// narrow on `policy.minimumRing !== null` and must never let a non-404 failure read as
// "no floor".

// ── Force Password Change ──────────────────────────────────────────────────
//
// The route is `password-change`, not `change-password`
// (`core-api/src/auth/auth.controller.ts` → `@Post('password-change')` under
// `@Controller('v1/auth')`). This is the user-side half of the forced-password-
// change flow, so the transposed path took the whole flow down. Route parity is
// enforced by `__tests__/core-api-route-parity.test.ts`.

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<LoginResponse> {
  return authedRequest<LoginResponse>('/v1/auth/password-change', {
    method: 'POST',
    headers: { ...sessionTransportHeaders() },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

// ── Admin: Org LLM Credentials (AI provider keys, seam S4) ──────────────────
// Org-scoped provider key management. Guarded server-side by
// `org:llm-credentials:manage` + org RLS (orgId from the JWT). The masking
// interceptor guarantees no plaintext / envelope fields are ever returned —
// the UI sees only the masked status projection + a boolean test result.

// ── WS-6 (compute-ownership-model) — app scaling timeline adapter (SM-5) ─────
